import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, Clock, Battery, Utensils, Moon, Activity, BookOpen, ListChecks, ShieldCheck, Zap, AlertCircle } from 'lucide-react';
// Modal Component for Privacy/Terms
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-2">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-3" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.2
    }} onClick={e => e.stopPropagation()} className="relative bg-[#1a2332] border border-[#2d3b4e] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#2d3b4e]" data-id="element-5">
          <h2 className="text-xl font-bold text-white" data-id="element-6">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#2d3b4e] transition-colors text-slate-400 hover:text-white" data-id="element-7">
            <X className="w-5 h-5" data-id="element-8" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] text-slate-300 space-y-4" data-id="element-9">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
// Privacy Policy Content
function PrivacyContent() {
  return <>
      <p className="text-sm text-slate-400" data-id="element-10">
        Última actualización: {new Date().toLocaleDateString('es-AR')}
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-11">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-12">
        Recopilamos información que nos proporcionás directamente, como tu
        nombre y correo electrónico cuando realizás una compra o te suscribís a
        nuestra lista.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-13">
        2. Uso de la Información
      </h3>
      <p data-id="element-14">Utilizamos la información recopilada para:</p>
      <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-15">
        <li data-id="element-16">Procesar tus compras y enviarte el producto digital</li>
        <li data-id="element-17">Enviarte actualizaciones y contenido relacionado</li>
        <li data-id="element-18">Mejorar nuestros productos y servicios</li>
        <li data-id="element-19">Responder a tus consultas</li>
      </ul>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-20">
        3. Protección de Datos
      </h3>
      <p data-id="element-21">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado, alteración
        o destrucción.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-22">4. Cookies</h3>
      <p data-id="element-23">
        Utilizamos cookies para mejorar tu experiencia en nuestro sitio. Podés
        configurar tu navegador para rechazar cookies, aunque esto puede afectar
        algunas funcionalidades.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-24">5. Terceros</h3>
      <p data-id="element-25">
        No vendemos ni compartimos tu información personal con terceros, excepto
        cuando sea necesario para procesar pagos o cumplir con obligaciones
        legales.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-26">6. Tus Derechos</h3>
      <p data-id="element-27">
        Tenés derecho a acceder, rectificar o eliminar tu información personal.
        Para ejercer estos derechos, contactanos a través de nuestros canales
        oficiales.
      </p>
    </>;
}
// Terms of Service Content
function TermsContent() {
  return <>
      <p className="text-sm text-slate-400" data-id="element-28">
        Última actualización: {new Date().toLocaleDateString('es-AR')}
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-29">
        1. Aceptación de Términos
      </h3>
      <p data-id="element-30">
        Al acceder y utilizar este sitio web y adquirir nuestros productos,
        aceptás estos términos y condiciones en su totalidad.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-31">
        2. Producto Digital
      </h3>
      <p data-id="element-32">
        El ebook "Reinicio Metabólico 40+" es un producto digital de información
        educativa. Los resultados pueden variar según cada persona y su
        compromiso con el programa.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-33">3. Uso Personal</h3>
      <p data-id="element-34">
        El contenido adquirido es para uso personal únicamente. Queda prohibida
        la reproducción, distribución o reventa del material sin autorización
        expresa.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-35">
        4. Garantía de Satisfacción
      </h3>
      <p data-id="element-36">
        Ofrecemos una garantía de satisfacción de 7 días. Si no estás conforme
        con el producto, podés solicitar un reembolso completo dentro de este
        período.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-37">
        5. Descargo de Responsabilidad
      </h3>
      <p data-id="element-38">
        Este producto no reemplaza el consejo médico profesional. Consultá con
        un profesional de la salud antes de comenzar cualquier programa de
        alimentación o ejercicio, especialmente si tenés condiciones médicas
        preexistentes.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-39">
        6. Propiedad Intelectual
      </h3>
      <p data-id="element-40">
        Todo el contenido, incluyendo textos, gráficos, logos y material
        descargable, está protegido por derechos de autor y es propiedad
        exclusiva de Reinicio Metabólico 40+.
      </p>

      <h3 className="text-lg font-semibold text-white mt-6" data-id="element-41">
        7. Modificaciones
      </h3>
      <p data-id="element-42">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Las modificaciones entrarán en vigor inmediatamente después de
        su publicación.
      </p>
    </>;
}
// Animation variants for scroll reveals
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Initialize Meta Pixel and fire PageView
  useEffect(() => {
    // Meta Pixel initialization
    const initPixel = () => {
      if (typeof window !== 'undefined' && !(window as any).fbq) {
        const n = ((window as any).fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        }) as any;
        if (!(window as any)._fbq) (window as any)._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        // Load the pixel script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
      }
      // Initialize and track PageView
      if ((window as any).fbq) {
        ;
        (window as any).fbq('init', '904375051696707');
        (window as any).fbq('track', 'PageView');
      }
    };
    initPixel();
  }, []);
  // Fire AddToCart event
  const handleAddToCart = () => {
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Reinicio Metabólico 40+',
        content_type: 'product',
        value: 9300,
        currency: 'ARS'
      });
    }
  };
  const scrollToCTA = () => {
    const element = document.getElementById('offer');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#0d1821] text-slate-300 font-sans selection:bg-[#4fd1c5] selection:text-[#0d1821]" data-id="element-43">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-id="element-44">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-20" data-id="element-45">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-[#4fd1c5] blur-[120px]" data-id="element-46" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] rounded-full bg-[#1a2332] blur-[100px]" data-id="element-47" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-48">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-49">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-8" data-id="element-50">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#1a2332] border border-[#2d3b4e] text-[#4fd1c5] text-sm font-medium tracking-wide" data-id="element-51">
                <span className="w-2 h-2 rounded-full bg-[#4fd1c5] mr-2 animate-pulse" data-id="element-52"></span>
                Especial para mayores de 40
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight" data-id="element-53">
                A los 40+ no te falta voluntad:{' '}
                <span className="text-[#4fd1c5]" data-id="element-54">te falta un sistema.</span>
              </h1>

              <p className="text-xl md:text-2xl text-slate-400 font-light leading-relaxed" data-id="element-55">
                21 días para desinflamar sin gym y sin dieta perfecta.
              </p>

              <div className="pt-4" data-id="element-56">
                <button onClick={scrollToCTA} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#0d1821] transition-all duration-200 bg-[#4fd1c5] rounded-lg hover:bg-[#3dbdb2] hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4fd1c5] focus:ring-offset-[#0d1821]" data-id="element-57">
                  QUIERO EL PROTOCOLO 3×7
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-58" />
                </button>
                <p className="mt-4 text-sm text-slate-500 flex items-center" data-id="element-59">
                  <ShieldCheck className="w-4 h-4 mr-1.5" data-id="element-60" />
                  Garantía de satisfacción de 7 días
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-61">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[#2d3b4e] bg-[#1a2332]/50 backdrop-blur-sm" data-id="element-62">
                <img src="https://cdn.magicpatterns.com/uploads/qcC2YErdkuVvvsJeHDcytr/heylloyd-image-1769993632463.png" alt="Hombre de 40+ leyendo el ebook Reinicio Metabólico en una tablet" className="w-full h-auto object-cover" data-id="element-63" />
                {/* Overlay gradient for better text integration if needed, but image is good */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1821]/40 to-transparent pointer-events-none" data-id="element-64" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OPENING LEAD - EMOTIONAL HOOK */}
      <section className="py-20 bg-[#1a2332]" data-id="element-65">
        <div className="max-w-3xl mx-auto px-6" data-id="element-66">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="space-y-8 text-lg md:text-xl leading-relaxed text-slate-300" data-id="element-67">
            <p className="font-medium text-[#4fd1c5]" data-id="element-68">
              Hay una humillación silenciosa que no se dice en voz alta:
            </p>
            <p data-id="element-69">
              Ponerte una prenda "normal" y sentir que el cuerpo te está
              discutiendo.
            </p>
            <p data-id="element-70">
              Y lo peor no es eso. Lo peor es pensar:{' '}
              <span className="italic text-white" data-id="element-71">
                "Estoy haciendo cosas… ¿por qué no cambia?"
              </span>
            </p>
            <div className="pl-6 border-l-4 border-[#4fd1c5] py-2 my-8 bg-[#0d1821]/30 rounded-r-lg" data-id="element-72">
              <p className="text-white font-medium" data-id="element-73">
                A los 40+ el problema rara vez es ignorancia. Es que te estás
                exigiendo con un plan que solo funciona cuando tu vida está
                tranquila.
              </p>
            </div>
            <p className="text-2xl font-bold text-white text-center pt-4" data-id="element-74">
              Este ebook es para cuando tu vida{' '}
              <span className="text-[#4fd1c5] underline decoration-wavy decoration-[#4fd1c5]/30 underline-offset-4" data-id="element-75">
                no
              </span>{' '}
              está tranquila.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-24 bg-[#0d1821] relative" data-id="element-76">
        <div className="max-w-5xl mx-auto px-6" data-id="element-77">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-78">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-79">
              Si sentís que "antes te funcionaba y ahora no"…
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-80">
              No estás solo/a. Tu metabolismo cambió, pero tu estrategia sigue
              siendo la misma.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-81">
            {/* BEFORE */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="bg-[#1a2332]/50 p-8 rounded-2xl border border-[#2d3b4e]" data-id="element-82">
              <h3 className="text-xl font-semibold text-slate-400 mb-6 flex items-center" data-id="element-83">
                <Clock className="w-5 h-5 mr-2" data-id="element-84" /> Antes podías:
              </h3>
              <ul className="space-y-4" data-id="element-85">
                {['"Cuidarte" dos semanas y ver cambios', 'Entrenar fuerte unos días y sentirte mejor', 'Ordenar un poco la comida y listo'].map((item, i) => <motion.li key={i} variants={fadeInUp} className="flex items-start text-slate-400" data-id="element-86">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-2.5 mr-3 shrink-0" data-id="element-87" />
                    {item}
                  </motion.li>)}
              </ul>
            </motion.div>

            {/* NOW */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="bg-[#1a2332] p-8 rounded-2xl border border-[#4fd1c5]/30 shadow-[0_0_30px_rgba(79,209,197,0.05)] relative overflow-hidden" data-id="element-88">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#4fd1c5]/10 rounded-bl-full" data-id="element-89" />
              <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-90">
                <AlertCircle className="w-5 h-5 mr-2 text-[#4fd1c5]" data-id="element-91" /> Ahora
                pasa esto:
              </h3>
              <ul className="space-y-4" data-id="element-92">
                {['Te inflamas fácil', 'Te da sueño a la tarde', 'Te cuesta arrancar', 'Te dura poco cualquier plan', 'Y cuando fallás un día, se te cae todo'].map((item, i) => <motion.li key={i} variants={fadeInUp} className="flex items-start text-slate-200" data-id="element-93">
                    <X className="w-5 h-5 text-red-400 mt-0.5 mr-3 shrink-0" data-id="element-94" />
                    {item}
                  </motion.li>)}
              </ul>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center mt-16" data-id="element-95">
            <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-96">
              No es falta de carácter.{' '}
              <span className="text-[#4fd1c5]" data-id="element-97">Es falta de sistema.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* THE CYCLE SECTION */}
      <section className="py-24 bg-[#1a2332]" data-id="element-98">
        <div className="max-w-4xl mx-auto px-6" data-id="element-99">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-100">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-101">
              El ciclo que te está atrapando (y te cansa)
            </h2>
          </motion.div>

          <div className="relative" data-id="element-102">
            {/* Connecting Line */}
            <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-[#2d3b4e] md:left-1/2 md:-ml-px" data-id="element-103" />

            <div className="space-y-12 relative" data-id="element-104">
              {[{
              title: 'Lunes perfecto',
              desc: '"Ahora sí."',
              icon: Zap
            }, {
              title: 'Plan extremo',
              desc: 'Saco todo, hago todo.',
              icon: X
            }, {
              title: 'Jueves',
              desc: 'Estrés, cansancio, vida real.',
              icon: Battery
            }, {
              title: '"Bueno, ya fue"',
              desc: 'El momento de quiebre.',
              icon: AlertCircle
            }, {
              title: 'Culpa + Castigo',
              desc: 'O abandono total.',
              icon: Moon
            }, {
              title: 'Reinicio con más reglas',
              desc: '...y se repite.',
              icon: Activity
            }].map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: i % 2 === 0 ? -50 : 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className={`flex items-center ${i % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-105">
                  <div className="flex-1 hidden md:block" data-id="element-106" />

                  <div className="relative z-10 flex items-center justify-center w-10 h-10 rounded-full bg-[#0d1821] border-2 border-[#4fd1c5] shrink-0" data-id="element-107">
                    <step.icon className="w-5 h-5 text-[#4fd1c5]" data-id="element-108" />
                  </div>

                  <div className={`flex-1 ml-6 md:ml-0 ${i % 2 === 0 ? 'md:mr-12 md:text-right' : 'md:ml-12'}`} data-id="element-109">
                    <h3 className="text-lg font-bold text-white" data-id="element-110">
                      {step.title}
                    </h3>
                    <p className="text-slate-400" data-id="element-111">{step.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mt-16 p-6 bg-[#0d1821] rounded-xl border border-[#2d3b4e] text-center" data-id="element-112">
            <p className="text-xl text-slate-300" data-id="element-113">
              Esto no se arregla con más fuerza de voluntad.{' '}
              <br className="hidden md:block" data-id="element-114" />
              <span className="text-white font-bold" data-id="element-115">
                Se arregla con una estrategia diseñada para la vida real.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION REVEAL */}
      <section className="py-24 bg-[#0d1821] overflow-hidden" data-id="element-116">
        <div className="max-w-6xl mx-auto px-6" data-id="element-117">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-118">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-119">
              <span className="text-[#4fd1c5] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-120">
                La Solución
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-121">
                Presento: <br data-id="element-122" />
                <span className="text-[#4fd1c5]" data-id="element-123">Reinicio Metabólico 40+</span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-124">
                Un ebook práctico para desinflamar, recuperar energía y volver a
                sentirte liviano/a en 21 días.
              </p>

              <div className="grid sm:grid-cols-2 gap-8" data-id="element-125">
                <div className="space-y-4" data-id="element-126">
                  <h4 className="font-bold text-red-400 uppercase text-sm tracking-wide border-b border-red-400/20 pb-2" data-id="element-127">
                    Sin (Lo viejo)
                  </h4>
                  <ul className="space-y-3" data-id="element-128">
                    {['Gimnasio', 'Dietas perfectas', 'Contar calorías obsesivamente', 'Recetas imposibles'].map((item, i) => <li key={i} className="flex items-center text-slate-400 line-through decoration-slate-600" data-id="element-129">
                        <X className="w-4 h-4 text-red-900/50 mr-2" data-id="element-130" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="space-y-4" data-id="element-131">
                  <h4 className="font-bold text-[#4fd1c5] uppercase text-sm tracking-wide border-b border-[#4fd1c5]/20 pb-2" data-id="element-132">
                    Con (Lo nuevo)
                  </h4>
                  <ul className="space-y-3" data-id="element-133">
                    {['Estructura', 'Mínimos efectivos', 'Repetición inteligente', 'Método anti-caos'].map((item, i) => <li key={i} className="flex items-center text-white font-medium" data-id="element-134">
                        <Check className="w-4 h-4 text-[#4fd1c5] mr-2" data-id="element-135" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-136">
              <div className="absolute inset-0 bg-[#4fd1c5] blur-[100px] opacity-10 rounded-full" data-id="element-137" />
              <div className="relative bg-[#1a2332] p-2 rounded-2xl border border-[#2d3b4e] shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-138">
                <img src="https://cdn.magicpatterns.com/uploads/qcC2YErdkuVvvsJeHDcytr/heylloyd-image-1769993632463.png" alt="Reinicio Metabólico Ebook Cover" className="w-full rounded-xl" data-id="element-139" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROTOCOL SECTION */}
      <section className="py-24 bg-[#1a2332]" data-id="element-140">
        <div className="max-w-6xl mx-auto px-6" data-id="element-141">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-142">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-143">
              La diferencia: el{' '}
              <span className="text-[#4fd1c5]" data-id="element-144">Protocolo 3×7</span>
            </h2>
            <p className="text-slate-400 text-lg" data-id="element-145">
              La mayoría intenta cambiar todo junto. Acá hacemos lo contrario.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-146">
            {[{
            title: '1. Comidas',
            desc: 'Primero ordenás lo que más impacta sin esfuerzo heroico.',
            icon: Utensils,
            color: 'text-blue-400',
            bg: 'bg-blue-400/10'
          }, {
            title: '2. Sueño / Estrés',
            desc: 'Después bajás la alarma interna que te empuja a comer y te roba energía.',
            icon: Moon,
            color: 'text-purple-400',
            bg: 'bg-purple-400/10'
          }, {
            title: '3. Movimiento',
            desc: 'Recién después activás el cuerpo con movimiento mínimo (sin lesionarte, sin gym).',
            icon: Activity,
            color: 'text-[#4fd1c5]',
            bg: 'bg-[#4fd1c5]/10'
          }].map((pillar, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="bg-[#0d1821] p-8 rounded-2xl border border-[#2d3b4e] hover:border-[#4fd1c5]/30 transition-colors group" data-id="element-147">
                <div className={`w-14 h-14 rounded-xl ${pillar.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} data-id="element-148">
                  <pillar.icon className={`w-7 h-7 ${pillar.color}`} data-id="element-149" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-150">
                  {pillar.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-151">{pillar.desc}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mt-16 text-center" data-id="element-152">
            <span className="inline-block px-6 py-3 rounded-full bg-[#4fd1c5]/10 border border-[#4fd1c5]/30 text-[#4fd1c5] font-bold text-lg" data-id="element-153">
              Microcambios acumulables = resultados sin rebote
            </span>
          </motion.div>
        </div>
      </section>

      {/* RESULTS SECTION */}
      <section className="py-24 bg-[#0d1821]" data-id="element-154">
        <div className="max-w-4xl mx-auto px-6" data-id="element-155">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-156">
            <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-157">
              Qué podés esperar sentir
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6" data-id="element-158">
            {['Menos hinchazón', 'Más energía sostenida', 'Menos ansiedad por picoteo', 'Más control sin rigidez', 'Un "reset" mental: "puedo sostener esto"'].map((result, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-center p-4 bg-[#1a2332] rounded-lg border border-[#2d3b4e]" data-id="element-159">
                <div className="w-8 h-8 rounded-full bg-[#4fd1c5]/20 flex items-center justify-center mr-4 shrink-0" data-id="element-160">
                  <Check className="w-5 h-5 text-[#4fd1c5]" data-id="element-161" />
                </div>
                <span className="text-slate-200 font-medium" data-id="element-162">{result}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-[#1a2332]" data-id="element-163">
        <div className="max-w-6xl mx-auto px-6" data-id="element-164">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-165">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-166">
              Qué incluye el ebook
            </h2>
            <p className="text-slate-400" data-id="element-167">
              Todo lo que necesitás para empezar hoy mismo
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-168">
            {[{
            text: 'Tablero del Reinicio (las 3 palancas)',
            icon: BookOpen
          }, {
            text: 'Preparación en 30 minutos',
            icon: Clock
          }, {
            text: 'Plan día a día de 21 días',
            icon: ListChecks
          }, {
            text: 'Menú base repetible "Argentina real"',
            icon: Utensils
          }, {
            text: 'Protocolo anti-antojos de 90 segundos',
            icon: ShieldCheck
          }, {
            text: 'Rutina de 12 minutos sin equipo',
            icon: Activity
          }, {
            text: 'Plan de mantenimiento 30 días',
            icon: Battery
          }, {
            text: 'Checklists imprimibles',
            icon: Check
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="p-6 bg-[#0d1821] rounded-xl border border-[#2d3b4e] flex flex-col items-center text-center hover:border-[#4fd1c5]/50 transition-colors" data-id="element-169">
                <item.icon className="w-8 h-8 text-[#4fd1c5] mb-4" data-id="element-170" />
                <p className="text-slate-300 font-medium" data-id="element-171">{item.text}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 bg-[#0d1821]" data-id="element-172">
        <div className="max-w-5xl mx-auto px-6" data-id="element-173">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-174">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-175">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center" data-id="element-176">
                <span className="w-8 h-8 rounded-full bg-[#4fd1c5] text-[#0d1821] flex items-center justify-center mr-3 text-sm" data-id="element-177">
                  ✓
                </span>
                Es para vos si:
              </h3>
              <ul className="space-y-4" data-id="element-178">
                {['Tenés 40+ y querés sentirte menos pesado/a e inflamado/a', 'Estás cansado/a de arrancar y abandonar', 'Querés un plan simple que sobreviva a tu vida real'].map((item, i) => <li key={i} className="flex items-start text-slate-300 p-4 bg-[#1a2332]/50 rounded-lg border-l-4 border-[#4fd1c5]" data-id="element-179">
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-180">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center" data-id="element-181">
                <span className="w-8 h-8 rounded-full bg-slate-700 text-slate-300 flex items-center justify-center mr-3 text-sm" data-id="element-182">
                  ✕
                </span>
                No es para vos si:
              </h3>
              <ul className="space-y-4" data-id="element-183">
                {['Querés resultados extremos en 7 días', 'Buscás una dieta rígida con reglas infinitas', 'No estás dispuesto/a a hacer microcambios consistentes'].map((item, i) => <li key={i} className="flex items-start text-slate-400 p-4 bg-[#1a2332]/30 rounded-lg border-l-4 border-slate-600" data-id="element-184">
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="offer" className="py-24 bg-[#1a2332] relative overflow-hidden" data-id="element-185">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2332] to-[#0d1821]" data-id="element-186" />

        {/* Decorative elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#4fd1c5] rounded-full blur-[150px] opacity-10 pointer-events-none" data-id="element-187" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-188">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-8" data-id="element-189">
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight" data-id="element-190">
              No estás fallando vos. <br data-id="element-191" />
              <span className="text-[#4fd1c5]" data-id="element-192">Está fallando el enfoque.</span>
            </h2>

            <p className="text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-193">
              No necesitás motivación. Necesitás un sistema que funcione cuando
              no tenés ganas.
            </p>

            <div className="bg-[#0d1821] p-8 md:p-12 rounded-3xl border border-[#4fd1c5]/30 shadow-2xl max-w-lg mx-auto mt-12 transform hover:scale-[1.02] transition-transform duration-300" data-id="element-194">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-195">
                <span className="text-slate-400 text-sm uppercase tracking-widest" data-id="element-196">
                  Oferta Especial
                </span>
                <span className="px-2 py-1 bg-[#4fd1c5] text-[#0d1821] text-xs font-bold rounded-full" data-id="element-197">
                  -84% OFF
                </span>
              </div>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-198">
                <span className="text-slate-500 line-through text-xl" data-id="element-199">
                  $57.000
                </span>
                <span className="text-5xl font-bold text-white" data-id="element-200">$9.300</span>
              </div>

              <a href="https://argrutinas.com/cart/42958860681425:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="w-full py-4 px-6 bg-[#4fd1c5] hover:bg-[#3dbdb2] text-[#0d1821] font-bold text-xl rounded-xl shadow-[0_0_20px_rgba(79,209,197,0.3)] hover:shadow-[0_0_30px_rgba(79,209,197,0.5)] transition-all duration-200 flex items-center justify-center group" data-id="element-201">
                QUIERO EL PROTOCOLO 3×7
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-202" />
              </a>

              <p className="mt-4 text-xs text-slate-500" data-id="element-203">
                Acceso inmediato • Pago seguro • Garantía de 7 días
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 bg-[#0d1821] border-t border-[#1a2332]" data-id="element-204">
        <div className="max-w-4xl mx-auto px-6" data-id="element-205">
          <div className="flex flex-col items-center space-y-4" data-id="element-206">
            <div className="flex items-center gap-6" data-id="element-207">
              <button onClick={() => setPrivacyOpen(true)} className="text-slate-500 hover:text-[#4fd1c5] transition-colors text-sm" data-id="element-208">
                Política de Privacidad
              </button>
              <span className="text-slate-700" data-id="element-209">•</span>
              <button onClick={() => setTermsOpen(true)} className="text-slate-500 hover:text-[#4fd1c5] transition-colors text-sm" data-id="element-210">
                Términos y Condiciones
              </button>
            </div>

            <p className="text-slate-600 text-sm text-center" data-id="element-211">
              © {new Date().getFullYear()} Reinicio Metabólico 40+. Todos los
              derechos reservados.
            </p>
            <p className="text-slate-700 text-xs text-center" data-id="element-212">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-213">
        <PrivacyContent data-id="element-214" />
      </LegalModal>

      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-215">
        <TermsContent data-id="element-216" />
      </LegalModal>
    </div>;
}